import { Check } from '@mui/icons-material';
import { CircularProgress, Grid } from '@mui/material';
import { validateEmail, validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalInfoIcon as InfoIcon,
  RoutalDialog,
  RoutalPalette,
  RoutalTab,
  RoutalTabs,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import WarningIcon from '../../../../assets/svg/WarningIcon';
import CopyLinkIcon from '../../../../assets/svg/dispatch-icons/CopyLinkIcon';
import EmailIcon from '../../../../assets/svg/dispatch-icons/EmailIcon';
import OpenTabIcon from '../../../../assets/svg/dispatch-icons/OpenTabIcon';
import WhatsAppIcon from '../../../../assets/svg/dispatch-icons/WhatsappIcon';
import { RouteAvatarName } from '../../../../components/RouteAvatarName';
import * as routing from '../../../../constants/Routing';
import { Driver } from '../../../../domain/driver/Driver';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveSupportDocs } from '../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import { useSearchVehicles } from '../../../../hooks/vehicle/useSearchVehicles';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import configuration from '../../../../services/configuration';
import eventsPanel from '../../../../services/events';
import { dispatchRouteByEmail } from '../../../../services/requests';
import { useRetrieveActivePlanDrivers } from '../hooks/plan-details/useRetrieveActivePlanDrivers';

const ActionIconSrc = ({
  act,
  size = 24,
  disabled = false,
  iconColor = RoutalPalette.primary80,
  hoverColor,
  onClick,
}: {
  act: ActionTypes;
  size?: number;
  disabled?: boolean;
  iconColor?: string;
  hoverColor?: string;
  onClick?: (...args: any[]) => any;
}) => (
  <Button
    variant="text"
    onClick={() => {
      if (!disabled && onClick) onClick();
    }}
    style={{ cursor: disabled ? `default` : `pointer`, opacity: disabled ? `0.5` : `1`, border: 0, padding: 0 }}
  >
    {act === `copy_link` ? (
      <CopyLinkIcon color={iconColor} hoverColor={hoverColor} style={{ width: size, height: size }} />
    ) : act === `send_email` ? (
      <EmailIcon color={iconColor} hoverColor={hoverColor} style={{ width: size, height: size }} />
    ) : act === `send_whatsapp` ? (
      <WhatsAppIcon color={iconColor} hoverColor={hoverColor} style={{ width: size, height: size }} />
    ) : act === `open_tab` ? (
      <OpenTabIcon color={iconColor} hoverColor={hoverColor} style={{ width: size, height: size }} />
    ) : null}
  </Button>
);

type DispatchDialogProps = {
  plan: PlanFlat;
  handleCloseDialog: (...args: any[]) => any;
  dispatchDialogOpen: boolean;
};

type ActionTypes = `send_whatsapp` | `send_email` | `copy_link` | `open_tab`;

export const DispatchDialog = ({ plan, handleCloseDialog, dispatchDialogOpen }: DispatchDialogProps) => {
  const navigate = useNavigate();

  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: paginatedVehicles } = useSearchVehicles({
    offset: 0,
    limit: 100,
  });

  const [localDrivers, setLocalDrivers] = useState<Driver[]>(planDrivers ?? []);
  const [sendToAllEmptyEmails, setSendToAllEmptyEmails] = useState<Driver[]>([]);
  const [copyFlags, setCopyFlags] = useState<string[]>([]);
  const [sendingFlags, setSendingFlags] = useState<string[]>([]);
  const [sentFlags, setSentFlags] = useState<string[]>([]);
  const [action, setAction] = useState<ActionTypes>(`copy_link`);
  const [actionHover, setActionHover] = useState<Record<ActionTypes, boolean>>({
    copy_link: false,
    send_email: false,
    send_whatsapp: false,
    open_tab: false,
  });

  useEffect(() => {
    if ((planDrivers ?? []).length > 0) {
      setLocalDrivers(planDrivers!);
    }

    const retrievedAction = localStorage.getItem(`DISPATCH_ACTION`);
    setAction(
      `${
        retrievedAction && [`copy_link`, `send_email`, `send_whatsapp`].includes(retrievedAction)
          ? retrievedAction
          : `copy_link`
      }` as ActionTypes
    );

    return () => {
      setLocalDrivers([]);
    };
  }, [planDrivers]);

  useEffect(() => {
    if (localDrivers && localDrivers.length > 0) {
      setSendToAllEmptyEmails(
        localDrivers.filter((driver) => !driver.email || driver.email === `` || !validateEmail(driver.email))
      );
    }
  }, [localDrivers]);

  if (!activeProject) {
    return null;
  }

  const driverLinkInfo = () => {
    const LABEL_FLAG = `info_dispatch`;

    const text = intl
      .formatMessage({ id: `dispatch_dialog.info_app_drivers` }, { driver_link: configuration.DRIVERS_ENDPOINT })
      .split(configuration.DRIVERS_ENDPOINT);

    return (
      <Grid>
        <Text tagName="span" style={{ fontSize: `14px` }}>
          {text[0]}
        </Text>
        <RoutalTooltip
          mouseLeaveDelay={0}
          id={configuration.DRIVERS_ENDPOINT}
          title={
            copyFlags.includes(LABEL_FLAG)
              ? intl.formatMessage({ id: `planner.copied` })
              : intl.formatMessage({ id: `planner.click_to_copy` })
          }
          placement="top"
        >
          <Text
            tagName="span"
            variant="button"
            onClick={() => {
              setCopyFlags([...copyFlags, LABEL_FLAG]);
              if (navigator.clipboard) navigator.clipboard.writeText(configuration.DRIVERS_ENDPOINT);
              else {
                const el = document.createElement(`textarea`);
                el.value = configuration.DRIVERS_ENDPOINT;
                const helper = document.getElementById(`helper_copy_assign`);
                if (!helper) return;

                helper.appendChild(el);
                el.select();
                document.execCommand(`copy`);
                helper.removeChild(el);
              }

              eventsPanel.planDispatchAction(`dispatch_copy_link`);
              setTimeout(() => {
                setCopyFlags(copyFlags);
              }, 2000);
            }}
            style={{ fontWeight: `bold`, fontSize: `15px` }}
          >
            {configuration.DRIVERS_ENDPOINT}
          </Text>
        </RoutalTooltip>
        <Text tagName="span" style={{ fontSize: `14px`, marginRight: `10px` }}>
          {text[1]}
        </Text>

        <span
          onClick={() =>
            window.open(retrieveDoc({ supportPage: `driverApp`, language: user?.language.value }), `_blank`)
          }
          style={{ fontWeight: `bold`, cursor: `pointer` }}
        >
          <InfoIcon
            color={RoutalPalette.primary40}
            style={{ height: `14px`, width: `14px`, verticalAlign: `middle` }}
          />
          <Text
            tagName="span"
            variant="button"
            style={{ fontWeight: `bold`, fontSize: `14px`, paddingLeft: `3px` }}
            intlId="planner.more_info"
          />
        </span>
      </Grid>
    );
  };

  const generateLink = (driver: Driver) => {
    return driver.getRouteMagicLink();
  };

  const handleCopyLink = (driver: Driver) => {
    if (driver) {
      const url = generateLink(driver);
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url);
      } else {
        const el = document.createElement(`textarea`);
        el.value = url;
        const helper = document.getElementById(`helper_copy_assign`);
        if (!helper) return;

        helper.appendChild(el);
        el.select();
        document.execCommand(`copy`);
        helper.removeChild(el);
      }

      eventsPanel.planDispatchAction(`dispatch_copy_link`);
    }
  };

  const handleSendEmail = async (driverId: string) => {
    setSendingFlags([...sendingFlags, driverId]);

    sendDriverEmail(driverId).finally(() => {
      setSendingFlags([...sendingFlags.filter((flag) => flag !== driverId)]);

      setSentFlags([...sentFlags, driverId]);
      setTimeout(() => {
        setSentFlags(sentFlags);
      }, 2000);
    });

    eventsPanel.planDispatchAction(`dispatch_send_email`);
  };

  const sendDriverEmail = async (driverId: string) => {
    return dispatchRouteByEmail(driverId).catch((error) => {
      console.error(`error`, error);
    });
  };

  const sendAllEmails = () => {
    const validDrivers = localDrivers.filter(
      (driver) => driver.email && driver.email !== `` && validateEmail(driver.email)
    );
    if (validDrivers.length > 0) {
      const loadingDrivers = [...sendingFlags, ...validDrivers.map((driver) => driver.id)];

      setSendingFlags(loadingDrivers);

      validDrivers.forEach((driver) => {
        sendDriverEmail(driver.id).finally(() => {
          setSendingFlags(sendingFlags.filter((flag: any) => flag !== driver.id));

          setSentFlags([...sentFlags, driver.id]);
          setTimeout(() => {
            setSentFlags(sentFlags);
          }, 2000);
        });
      });

      eventsPanel.planDispatchAction(`dispatch_send_email_to_all`);
    }
  };

  const getDriverLinkedVehiclePhone = (driver: Driver) => {
    const vehicle = (paginatedVehicles?.docs ?? []).find((v) => v.id === driver.vehicle_id);
    if (vehicle?.phone && validatePhoneNumber(vehicle.phone)) {
      return vehicle.phone;
    }
    return ``;
  };

  const canViewMagicLinks = () => {
    if (plan.status === `in_progress`) return true;

    if (
      !activeProject.drivers.configHasField(`visualize_planning_plans`) &&
      (plan.status === `planning` || plan.status === `draft`)
    ) {
      return false;
    }

    if (!activeProject.drivers.configHasField(`visualize_finished_plans`) && plan.status === `finished`) {
      return false;
    }

    return true;
  };

  return (
    <RoutalDialog
      id="dispatch-dialog"
      open={dispatchDialogOpen}
      dialogProps={{
        style: {
          minHeight: `400px`,
        },
      }}
      onCloseDialog={() => {
        setLocalDrivers(planDrivers ?? []);
        setSendToAllEmptyEmails([]);
        setCopyFlags([]);
        setSendingFlags([]);
        handleCloseDialog();
      }}
      titleContent={
        <Grid style={{ padding: `14px 0px` }}>
          <Text variant="h6" intlId="dispatch_dialog.title" />
        </Grid>
      }
      footerContent={
        <Grid style={{ width: `100%`, marginBottom: `8px` }}>
          {action === `send_email` ? (
            <>
              {activeProject.planner.vehicleFieldsHasSpecificField(`email`) ? (
                <Grid container alignItems="center" spacing={2}>
                  <Grid item sm={12} md={sendToAllEmptyEmails.length !== localDrivers.length ? 8 : 12}>
                    <Grid container alignItems="center" direction="row" style={{ flexWrap: `unset` }}>
                      {sendToAllEmptyEmails.length > 0 && (
                        <WarningIcon
                          style={{ color: RoutalPalette.error.medium, fontSize: `24px`, marginRight: `8px` }}
                        />
                      )}

                      {sendToAllEmptyEmails.length === 0 ? (
                        <Text intlId="dispatch_dialog.send_email_to_all_info" />
                      ) : sendToAllEmptyEmails.length === localDrivers.length ? (
                        <Text intlId="dispatch_dialog.no_drivers_with_valid_email" />
                      ) : (
                        <Text
                          intlId="dispatch_dialog.send_email_to_all_error"
                          intlValues={{
                            drivers: sendToAllEmptyEmails.map((driver) => driver.label).join(`, `),
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>

                  {sendToAllEmptyEmails.length !== localDrivers.length && (
                    <Grid item sm={12} md={4}>
                      <Grid container direction="row" justifyContent="flex-end">
                        <Button
                          onClick={() => {
                            sendAllEmails();
                          }}
                          disabled={sendingFlags.length > 0}
                          intlId={
                            sendToAllEmptyEmails.length === 0
                              ? `dispatch_dialog.button_send_email_to_all`
                              : `dispatch_dialog.button_send_email_to_all_anyway`
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid item sm={12} md={sendToAllEmptyEmails.length !== localDrivers.length ? 8 : 12}>
                  <Grid container alignItems="center" direction="row" style={{ flexWrap: `unset` }}>
                    <WarningIcon style={{ color: RoutalPalette.error.medium, fontSize: `24px`, marginRight: `8px` }} />

                    <HSpacer small />

                    <div style={{ fontSize: `14px` }}>
                      {userCredentials?.projects.test(activeProject.id, `can_update_project`) ? (
                        <>
                          <Text tagName="span" intlId="dispatch_dialog.user_view_email_not_enabled.1" />

                          <Text
                            tagName="span"
                            variant="button"
                            onClick={() => {
                              navigate(
                                `${routing.PROJECT_SETTINGS.PLANNER.VEHICLES.replace(`:projectId`, activeProject.id)}`
                              );
                            }}
                            intlId="dispatch_dialog.user_view_email_not_enabled.2"
                          />
                        </>
                      ) : (
                        <Text tagName="span" intlId="dispatch_dialog.user_view_email_not_enabled.not_privileged" />
                      )}
                    </div>
                  </Grid>
                </Grid>
              )}
            </>
          ) : null}
        </Grid>
      }
    >
      <div id="helper_copy_assign" />

      <Grid
        item
        container
        direction="row"
        wrap="nowrap"
        alignItems="center"
        justifyContent="space-between"
        style={{
          padding: `20px`,
          borderRadius: `8px`,
          backgroundColor: RoutalPalette.primary20n,
        }}
      >
        {driverLinkInfo()}
      </Grid>

      <VSpacer medium />

      {canViewMagicLinks() ? (
        <>
          <Grid height={`40px`}>
            <RoutalTabs
              value={action}
              onChange={(event: React.SyntheticEvent, newValue: string) => {
                localStorage.setItem(`DISPATCH_ACTION`, newValue);
                setAction(newValue as ActionTypes);
              }}
            >
              {([`copy_link`, `send_email`, `send_whatsapp`] as ActionTypes[]).map((act) => {
                return (
                  <RoutalTab
                    value={act}
                    iconPosition="start"
                    icon={
                      <Grid style={{ display: `flex`, flexDirection: `row` }}>
                        <ActionIconSrc
                          act={act}
                          size={16}
                          iconColor={
                            actionHover[act]
                              ? RoutalPalette.primary20
                              : act === action
                              ? RoutalPalette.primary40
                              : RoutalPalette.neutral60
                          }
                        />
                      </Grid>
                    }
                    setHover={(newHover) => {
                      setActionHover({ ...actionHover, [act]: newHover });
                    }}
                    label={intl.formatMessage({ id: `dispatch_dialog.action.${act}` })}
                  />
                );
              })}
            </RoutalTabs>
          </Grid>

          <div
            style={{
              maxHeight: `400px`,
              overflowY: `auto` as any,
              overflowX: `hidden` as any,
            }}
          >
            <Grid container direction="column">
              {localDrivers.map((driver: Driver, idx: number, driversArray: Driver[]) => {
                return (
                  <Grid
                    key={`assign-driver-${driver.id}`}
                    container
                    direction="row"
                    alignItems="center"
                    style={{
                      padding: `8px 0`,
                      paddingRight: `8px`,
                      borderTop: `1px solid ${RoutalPalette.grey4}`,
                      ...(idx === localDrivers.length - 1 ? { borderBottom: `1px solid ${RoutalPalette.grey4}` } : {}),
                    }}
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap">
                      <RouteAvatarName name={driver.getShortLabel()} color={driver.color?.value} />

                      <HSpacer small />

                      <RoutalTooltip
                        mouseEnterDelay={0.5}
                        mouseLeaveDelay={0}
                        id={driver.id}
                        title={driver.label ?? `No name`}
                        placement="top"
                      >
                        {driver.label && driver.label !== `` ? (
                          <Text variant="body" isBold>
                            {driver.label.length > 20 ? `${driver.label.substring(0, 20)}...` : driver.label}
                          </Text>
                        ) : (
                          <Text variant="label" intlId="no_name" style={{ color: RoutalPalette.secondary00 }} />
                        )}
                      </RoutalTooltip>
                    </Grid>

                    <HSpacer medium />

                    {driver.phone && (
                      <>
                        <Grid container justifyContent="center" wrap="nowrap">
                          <Text variant="body">{driver.phone}</Text>
                        </Grid>

                        <HSpacer medium />
                      </>
                    )}

                    <Grid container justifyContent="flex-end" wrap="nowrap">
                      {activeProject.planner.vehicleFieldsHasSpecificField(`phone`) && action === `send_whatsapp` ? (
                        <RoutalTooltip
                          title={
                            <Text
                              style={{ color: `white` }}
                              intlId={
                                getDriverLinkedVehiclePhone(driver) !== ``
                                  ? `dispatch_dialog.button_send_whatsapp`
                                  : `dispatch_dialog.not_valid_phone`
                              }
                            />
                          }
                        >
                          <ActionIconSrc
                            act={`send_whatsapp`}
                            size={23}
                            disabled={getDriverLinkedVehiclePhone(driver) === ``}
                            hoverColor={RoutalPalette.primary40}
                            onClick={() => {
                              eventsPanel.planDispatchAction(`dispatch_send_whatsapp`);
                              const url = encodeURIComponent(generateLink(driver));
                              window.open(
                                `https://api.whatsapp.com/send?phone=${getDriverLinkedVehiclePhone(
                                  driver
                                )}&text=${url}`,
                                `_blank`
                              );
                            }}
                          />
                        </RoutalTooltip>
                      ) : null}

                      {activeProject.planner.vehicleFieldsHasSpecificField(`email`) && action === `send_email` ? (
                        <RoutalTooltip
                          title={
                            <Text
                              style={{ color: `white` }}
                              intlId={`dispatch_dialog.button_send_email${
                                !driver.email || !validateEmail(driver.email) ? `_disabled` : ``
                              }`}
                            />
                          }
                        >
                          <Grid display={`flex`} alignItems={`center`} style={{ height: `30px` }}>
                            {!sendingFlags.includes(driver.id) && !sentFlags.includes(driver.id) && (
                              <ActionIconSrc
                                act={`send_email`}
                                size={22}
                                disabled={!driver.email || !validateEmail(driver.email)}
                                hoverColor={RoutalPalette.primary40}
                                onClick={() => {
                                  handleSendEmail(driver.id);
                                }}
                              />
                            )}
                            {sendingFlags.includes(driver.id) && <CircularProgress size={24} />}
                            {sentFlags.includes(driver.id) && (
                              <Check style={{ color: RoutalPalette.success.medium, fontSize: `24px` }} />
                            )}
                          </Grid>
                        </RoutalTooltip>
                      ) : null}

                      {action === `copy_link` ? (
                        <RoutalTooltip
                          title={<Text style={{ color: `white` }} intlId={`dispatch_dialog.button_copy_link`} />}
                        >
                          <Grid display={`flex`} alignItems={`center`} style={{ height: `30px` }}>
                            {!copyFlags.includes(driver.id) && (
                              <ActionIconSrc
                                act={`copy_link`}
                                size={22}
                                hoverColor={RoutalPalette.primary40}
                                onClick={() => {
                                  // setCopyIconDriverId(driver.id);
                                  setCopyFlags([...copyFlags, driver.id]);
                                  handleCopyLink(driver);
                                  setTimeout(() => {
                                    setCopyFlags(copyFlags);
                                  }, 2000);
                                }}
                              />
                            )}
                            {copyFlags.includes(driver.id) && (
                              <Check
                                style={{
                                  color: RoutalPalette.success.medium,
                                  fontSize: `24px`,
                                }}
                              />
                            )}
                          </Grid>
                        </RoutalTooltip>
                      ) : null}
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        </>
      ) : (
        <Grid height={`40px`} container direction="row" justifyContent="flex-start" alignItems="center">
          <Text
            tagName="span"
            intlId="dispatch_dialog.routes_visualization_not_allowed"
            intlValues={{
              a: (chunks: any) => (
                <Text
                  isBold
                  tagName="span"
                  variant="button"
                  onClick={() =>
                    navigate(`${routing.DRIVERS.APP.CONFIGURATION.replace(`:projectId`, `${activeProject.id}`)}`)
                  }
                >
                  {chunks}
                </Text>
              ),
            }}
          />
        </Grid>
      )}
    </RoutalDialog>
  );
};
